import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { http } from "@/lib/api";
import { Search, ExternalLink, Sparkles, UserPlus } from "lucide-react";
import WorldChangerNominateModal from "@/components/WorldChangerNominateModal";

// Curated chip set — subset of the full 28-cat taxonomy shown in the UI.
const CATEGORY_CHIPS = [
  "All", "Peace & Human Rights", "Humanitarian Aid", "Medicine & Healthcare",
  "Science", "Environment & Conservation", "Education", "Technology & Innovation",
  "Social Entrepreneurship", "Philanthropy", "Civil Rights", "Women's Rights",
  "Children's Rights", "Space", "Arts & Culture", "Journalism",
  "Faith & Service", "Public Service", "Climate Action", "Animal Welfare",
  "Clean Water", "Housing", "Business for Good", "Disability Advocacy",
];

function ChangerPortrait({ c, size = 72 }) {
  const [failed, setFailed] = useState(false);
  const monogram = (c.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const style = { width: size, height: size };
  if (failed || !c.photo_url) {
    return (
      <div className="rounded-full grid place-items-center shrink-0 font-mono font-bold" style={{ ...style, background: "var(--wofy-surface)", color: "var(--wofy-primary)", fontSize: size / 3 }}>
        {monogram}
      </div>
    );
  }
  return (
    <div className="rounded-full overflow-hidden shrink-0 border grid place-items-center" style={{ ...style, borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}>
      <img
        src={c.photo_url}
        alt={c.name}
        className="w-full h-full object-cover"
        loading="lazy"
        onError={() => setFailed(true)}
      />
    </div>
  );
}

export default function WorldChangers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [living, setLiving] = useState("all"); // all | living | historical
  const [nominateOpen, setNominateOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 500 };
    if (cat && cat !== "All") params.category = cat;
    if (q.trim()) params.q = q.trim();
    if (living === "living") params.living = true;
    if (living === "historical") params.living = false;
    const t = setTimeout(() => {
      http.get("/world-changers", { params })
        .then((r) => setItems(r.data))
        .finally(() => setLoading(false));
    }, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [cat, q, living]);

  const nationalityCount = useMemo(() => new Set(items.map((i) => i.nationality).filter(Boolean)).size, [items]);

  return (
    <div data-testid="world-changers-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
      <Helmet>
        <title>World Changers · Social Pulse</title>
        <meta name="description" content="Celebrating the people who changed lives, communities, nations and the world — a permanent knowledge graph of human achievement." />
        <link rel="canonical" href="https://worldofjoy.com/world-changers" />
        <meta property="og:title" content="World Changers · Social Pulse" />
        <meta property="og:description" content="Celebrating the people who changed lives, communities, nations and the world." />
        <meta property="og:url" content="https://worldofjoy.com/world-changers" />
      </Helmet>

      <div className="overline mb-3">WORLD CHANGERS</div>
      <div className="flex flex-wrap items-start justify-between gap-6">
        <h1 className="font-display text-4xl md:text-5xl max-w-3xl">
          Celebrating the people who changed lives, communities, nations and the world.
        </h1>
        <button
          data-testid="wc-nominate-btn"
          onClick={() => setNominateOpen(true)}
          className="wofy-btn-primary shrink-0"
        >
          <UserPlus className="w-4 h-4" /> Nominate someone
        </button>
      </div>
      <p className="mt-4 max-w-2xl text-base" style={{ color: "var(--wofy-text-soft)" }}>
        A permanent, growing collection of people whose work has improved humanity through compassion,
        leadership, science, medicine, education, humanitarian service, environmental protection, arts, and community service.
        {" "}Every profile links to Wikipedia and includes an AI-woven biographical essay.
      </p>

      {/* Search + living filter */}
      <div className="mt-8 flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1 max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-50" />
          <input
            data-testid="changers-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, nationality, field, or the difference they made…"
            className="w-full pl-10 pr-3 py-2.5 rounded-full border bg-transparent text-sm focus:outline-none focus:ring-2"
            style={{ borderColor: "var(--wofy-border)" }}
          />
        </div>
        <select
          data-testid="changers-living"
          value={living}
          onChange={(e) => setLiving(e.target.value)}
          className="rounded-full px-4 py-2.5 border text-sm bg-transparent"
          style={{ borderColor: "var(--wofy-border)" }}
        >
          <option value="all">All eras</option>
          <option value="living">Living today</option>
          <option value="historical">Historical figures</option>
        </select>
      </div>

      {/* Category chips */}
      <div className="mt-5 flex gap-2 flex-wrap">
        {CATEGORY_CHIPS.map((c) => (
          <button
            key={c}
            data-testid={`changers-cat-${c.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
            onClick={() => setCat(c)}
            className={`rounded-full px-3.5 py-1.5 text-xs border transition-colors ${cat === c ? "bg-[color:var(--wofy-text)] text-[color:var(--wofy-bg)] border-transparent" : "hover:bg-[color:var(--wofy-surface)]"}`}
            style={{ borderColor: "var(--wofy-border)" }}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="mt-6 text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
        {loading ? "Loading…" : `Showing ${items.length} World Changer${items.length === 1 ? "" : "s"} · ${nationalityCount} nationalities`}
      </div>

      <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((c) => (
          <div
            key={c.id}
            data-testid={`changer-${c.slug}`}
            className="wofy-card !p-0 overflow-hidden group flex flex-col"
          >
            <div className="p-6 flex items-start gap-4">
              <ChangerPortrait c={c} size={72} />
              <div className="min-w-0 flex-1">
                <Link to={`/world-changers/${c.slug}`} data-testid={`changer-name-${c.slug}`} className="font-display text-lg leading-tight line-clamp-2 hover:underline">
                  {c.name}
                </Link>
                <div className="text-[11px] font-mono uppercase tracking-wider mt-1 opacity-60">
                  {[c.nationality, c.born && c.born.slice(0, 4), c.died && c.died.slice(0, 4) ? `– ${c.died.slice(0,4)}` : (c.born ? "– present" : "")].filter(Boolean).join(" · ")}
                </div>
                <div className="text-[11px] font-mono uppercase tracking-wider mt-1 opacity-70">{c.occupation}</div>
              </div>
            </div>
            {c.tagline && (
              <div className="px-6 pb-4 flex-1">
                <p className="text-sm line-clamp-3" style={{ color: "var(--wofy-text-soft)" }}>{c.tagline}</p>
              </div>
            )}
            {c.categories?.length > 0 && (
              <div className="px-6 pb-4 flex flex-wrap gap-1.5">
                {c.categories.slice(0, 3).map((k) => (
                  <span key={k} className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: "var(--wofy-surface)", color: "var(--wofy-text-soft)" }}>{k}</span>
                ))}
                {c.categories.length > 3 && <span className="text-[10px] font-mono opacity-50">+{c.categories.length - 3}</span>}
              </div>
            )}
            <div className="mt-auto border-t px-6 py-3 flex items-center justify-between gap-3 text-[11px] font-mono" style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}>
              <Link
                to={`/world-changers/${c.slug}`}
                data-testid={`changer-profile-${c.slug}`}
                className="uppercase tracking-wider whitespace-nowrap"
                style={{ color: "var(--wofy-primary)" }}
              >
                Read profile →
              </Link>
              {c.wiki_url && (
                <a
                  href={c.wiki_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 opacity-70 hover:opacity-100"
                >
                  Wikipedia <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      {!loading && items.length === 0 && (
        <div className="mt-10 wofy-card text-center py-10">
          <div className="font-display text-xl">No World Changers match your filters.</div>
          <button onClick={() => { setCat("All"); setQ(""); setLiving("all"); }} className="wofy-btn-ghost mt-4">Reset filters</button>
        </div>
      )}

      <div className="mt-14 text-[11px] font-mono uppercase tracking-wider opacity-60 inline-flex items-center gap-1">
        <Sparkles className="w-3 h-3" /> Every profile includes an AI-woven biography synthesised from public sources. Wikipedia links open in a new tab.
      </div>

      <WorldChangerNominateModal open={nominateOpen} onClose={() => setNominateOpen(false)} />
    </div>
  );
}
